import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { webBadgeVariantClassName } from '@emovia/design-system';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          `${webBadgeVariantClassName('default')} [a&]:hover:bg-primary/90 border-transparent`,
        secondary:
          `${webBadgeVariantClassName('secondary')} [a&]:hover:bg-secondary/90 border-transparent`,
        destructive:
          `${webBadgeVariantClassName('destructive')} [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 border-transparent`,
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        emergency:
          'bg-emergency text-emergency-foreground [a&]:hover:bg-emergency/90 border-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type BadgeProps = ComponentPropsWithoutRef<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean };

const Badge = forwardRef<ComponentRef<'span'>, BadgeProps>(
  ({ className, variant, asChild = false, ...props }, ref) => {
    const Comp = (asChild ? Slot : 'span') as React.ElementType;

    return (
      <Comp
        ref={ref}
        data-slot="badge"
        className={cn(badgeVariants({ variant }), className)}
        {...props}
      />
    );
  },
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants, type BadgeProps };
